// src/componentes/Escala11/Competicao/CompetitionDetailsModal/index.tsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";

// Certifique-se de que os caminhos estão corretos:
import CompetitionPositionManager from "../CompetitionPositionManager";
import CompetitionLineupsTab from "../CompetitionLineupsTab";

// =================================================================
// TIPAGENS MANTIDAS
// =================================================================

interface CompetitionResults {
  message: string;
  competitionId: string;
  page: number;
  limit: number;
}

interface CompetitionWinners {
  message: string;
  competitionId: string;
  // Se a API retornar uma lista de vencedores, adicione a tipagem aqui (ex: winners: WinnerItem[];)
}

interface Player {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  isActive: boolean;
  positionId: string;
  createdAt: string;
  updatedAt: string;
}

interface Position {
  id: string;
  name: string;
  maxSelections: number;
  pointWeight: number;
  displayOrder: number;
  players: Player[];
}
interface TargetDraw {
  title: string;
  description: string;
  totalPrize: string;
  drawDate: string;
}
export interface Competition {
  id: string;
  title: string;
  description: string;
  scaleType: string;
  participationPrice: string;
  startDate: string;
  closeDate: string;
  status: string; // Pode ser 'DRAFT'
  targetDraw: TargetDraw;
  positions: Position[];
  winnersCount: number;
  positionsCount: number;
  playersCount: number;
  targetDrawId: string;
  createdAt: string;
  updatedAt: string;
}

interface CompetitionDetailsModalProps {
  token: string;
  open: boolean;
  onClose: () => void;
  initialCompetition: Competition;
  formatCurrency: (price: string) => string;
  onCompetitionUpdated: (updatedCompetition: Competition) => void;
  onCompetitionDeleted: (deletedId: string) => void;
}

const API_BASE_URL = "https://api.mercadoterra.com.br/admin/scale-competitions";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const CompetitionDetailsModal: React.FC<CompetitionDetailsModalProps> = ({
  token,
  open,
  onClose,
  initialCompetition,
  formatCurrency,
  onCompetitionUpdated,
  onCompetitionDeleted,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [competition, setCompetition] =
    useState<Competition>(initialCompetition);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [tabValue, setTabValue] = useState(0);

  const [resultsData, setResultsData] = useState<CompetitionResults | null>(
    null
  );
  const [isResultsLoading, setIsResultsLoading] = useState(false);

  const [winnersData, setWinnersData] = useState<CompetitionWinners | null>(
    null
  );
  const [isWinnersLoading, setIsWinnersLoading] = useState(false);

  const [editTitle, setEditTitle] = useState(initialCompetition.title);
  const [editWinnersCount, setEditWinnersCount] = useState(
    initialCompetition.winnersCount
  );

  const [feedback, setFeedback] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({ open: false, message: "", severity: "success" });
  const handleCloseFeedback = () => setFeedback({ ...feedback, open: false });

  //TRATAMENTO DE HORA PARA AJUSTAR CONFORME RETORNO DA API, PARA DISPLAY AJUSTADO AO USUÁRIO (HORÁRIO MOMENTANEAMENTE INCORRETO)
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        //timezone não é necessário após correção
        timeZone: "America/Sao_Paulo",
      });
    } catch {
      return "Data Inválida";
    }
  };

  const getStatusProps = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return { label: "ATIVA", color: "success" as const };
      case "DRAFT":
        return { label: "RASCUNHO", color: "warning" as const };
      case "PRIZES_DISTRIBUTED":
        return { label: "ENCERRADA", color: "error" as const };
      default:
        return { label: status, color: "default" as const };
    }
  };

  const statusProps = getStatusProps(competition.status);

  // --- Funções de API e Handlers (inalteradas) ---
  const fetchCompetitionData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${initialCompetition.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Falha ao carregar detalhes da competição.");
      }
      const updatedData: Competition = await response.json();

      setCompetition(updatedData);
      onCompetitionUpdated(updatedData);

      setEditTitle(updatedData.title);
      setEditWinnersCount(updatedData.winnersCount);
    } catch (error) {
      console.error("Erro ao buscar dados da competição:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompetitionResults = async (id: string) => {
    setIsResultsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${id}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Falha ao carregar detalhes da competição.");
      }
      const data: CompetitionResults = await response.json();
      setResultsData(data);
    } catch (error) {
      console.error("Erro ao buscar resultados:", error);
      setFeedback({
        open: true,
        message: `Falha ao carregar resultados: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsResultsLoading(false);
    }
  };

  const fetchCompetitionWinners = async (id: string) => {
    if (winnersData && !isWinnersLoading) return;

    setIsWinnersLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${id}/winners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Falha ao carregar a lista de ganhadores.");
      }
      const data: CompetitionWinners = await response.json();
      setWinnersData(data);
    } catch (error) {
      console.error("Erro ao buscar ganhadores:", error);
      setFeedback({
        open: true,
        message: `Falha ao carregar ganhadores: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsWinnersLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCompetitionData();
      setTabValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, initialCompetition.id]);

  const handlePositionsChanged = () => {
    fetchCompetitionData();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 2) {
      fetchCompetitionResults(competition.id);
    }
    if (newValue === 3) {
      fetchCompetitionWinners(competition.id);
    }
  };

  const handlePublishCompetition = async () => {
    setIsPublishing(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/${competition.id}/publish`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const errorMessage =
          errorBody.message || "Erro desconhecido ao publicar a competição.";
        throw new Error(errorMessage);
      }

      await fetchCompetitionData();
      setFeedback({
        open: true,
        message: `Competição "${competition.title}" publicada com sucesso!`,
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao publicar competição:", error);
      setFeedback({
        open: true,
        message: `Falha ao publicar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditTitle(competition.title);
    setEditWinnersCount(competition.winnersCount);
    setIsEditModalOpen(true);
  };

  const handleEditCompetition = async () => {
    if (!editTitle || Number(editWinnersCount) < 1) {
      setFeedback({
        open: true,
        message: "O título e a contagem de vencedores são obrigatórios.",
        severity: "error",
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${competition.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: editTitle,
          winnersCount: Number(editWinnersCount),
        }),
      });
      if (!response.ok) {
        throw new Error("Erro ao editar competição.");
      }

      setIsEditModalOpen(false);
      await fetchCompetitionData();
      setFeedback({
        open: true,
        message: "Competição editada com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao editar competição:", error);
      setFeedback({
        open: true,
        message: `Falha ao editar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompetition = async () => {
    const isConfirmed = window.confirm(
      `Tem certeza que deseja DELETAR a competição "${competition.title}"?`
    );
    if (!isConfirmed) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${competition.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status !== 204 && !response.ok) {
        throw new Error("Erro ao deletar competição.");
      }

      setFeedback({
        open: true,
        message: `Competição "${competition.title}" deletada com sucesso!`,
        severity: "success",
      });
      onClose();
      onCompetitionDeleted(competition.id);
    } catch (error) {
      console.error("Erro ao deletar competição:", error);
      setFeedback({
        open: true,
        message: `Falha ao deletar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Componente reusável para os botões de Ação (inalterado) ---
  const ActionButtons = (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexShrink: 0,
        flexWrap: "wrap",
        // Modo Mobile (fullScreen)
        ...(fullScreen && {
          flexDirection: "column",
          width: "100%",
          gap: 1.5,
        }),
      }}
    >
      {/* Botão de Publicar, visível apenas se for DRAFT */}
      {competition.status === "DRAFT" && (
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handlePublishCompetition}
          disabled={isLoading || isDeleting || isPublishing}
          sx={{
            width: fullScreen ? "100%" : "auto",
            backgroundColor: "#008870",
            "&:hover": { backgroundColor: "#005848ff" },
          }}
        >
          {isPublishing ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Publicar"
          )}
        </Button>
      )}

      <Button
        variant="outlined"
        startIcon={<EditIcon />}
        onClick={handleOpenEditModal}
        disabled={isLoading || isDeleting || isPublishing}
        sx={{
          width: fullScreen ? "100%" : "auto",
          color: "#008870",
          borderColor: "#008870",
          "&:hover": {
            color: "#005848ff",
            borderColor: "#005848ff",
          },
        }}
      >
        Editar
      </Button>
      <Button
        variant="contained"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={handleDeleteCompetition}
        disabled={isLoading || isDeleting || isPublishing}
        sx={{ width: fullScreen ? "100%" : "auto" }}
      >
        {isDeleting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Deletar"
        )}
      </Button>
    </Box>
  );

  return (
    <>
      {/* 1. DIALOG PRINCIPAL (Visualização) */}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        fullScreen={fullScreen}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: fullScreen ? 2 : 0 }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontWeight: "bold",
                flexGrow: 1,
                mr: fullScreen ? 1 : 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: fullScreen ? "normal" : "nowrap",
              }}
            >
              {competition.title}
            </Typography>

            {/* BOTÃO DE FECHAR ('X'): VISÍVEL APENAS EM TELAS PEQUENAS */}
            {fullScreen && (
              <IconButton
                onClick={onClose}
                aria-label="fechar modal"
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon />
              </IconButton>
            )}

            {/* BOTÕES DE AÇÃO: APENAS EM TELAS MAIORES */}
            {!fullScreen && ActionButtons}
          </Box>
        </DialogTitle>

        {/* CONTROLE DE ABAS */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="abas de detalhes da competição"
            variant={fullScreen ? "scrollable" : "standard"}
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "&.Mui-selected": { color: "#008870" },
            }}
          >
            <Tab label="Detalhes & Posições" />
            <Tab label="Escalações" />
            <Tab label="Resultados" />
            <Tab label="Ganhadores" />
          </Tabs>
        </Box>

        <DialogContent
          dividers
          sx={{
            maxHeight: fullScreen ? "calc(100vh - 120px)" : "80vh",
            overflowY: "auto",
          }}
        >
          {/* Conteúdo da Aba 0: Detalhes e Posições */}
          <CustomTabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography
                variant="body1"
                color={statusProps.color}
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Status: {statusProps.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {competition.description}
              </Typography>
            </Box>

            {/* Detalhes e Sorteio Vinculado - SUBSTITUINDO GRID */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2, // Simula spacing={2}
              }}
            >
              {/* Coluna 1: Detalhes da Competição (xs=12, sm=6) */}
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                <Typography variant="h6" gutterBottom>
                  Detalhes da Competição
                </Typography>
                <Typography variant="body1">
                  Tipo de Escala: {competition.scaleType}
                </Typography>
                <Typography variant="body1">
                  Preço de Participação:{" "}
                  {formatCurrency(competition.participationPrice)}
                </Typography>
                <Typography variant="body1">
                  Início: {formatDateTime(competition.startDate)}
                </Typography>
                <Typography variant="body1">
                  Encerramento: {formatDateTime(competition.closeDate)}
                </Typography>
                <Typography variant="body1">
                  Vencedores: {competition.winnersCount}
                </Typography>
                <Typography variant="body1">
                  Total de Posições Criadas: {competition.positionsCount}
                </Typography>
                <Typography variant="body1">
                  Total de Itens: {competition.playersCount}
                </Typography>
              </Box>

              {/* Coluna 2: Sorteio Vinculado (xs=12, sm=6) */}
              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                <Typography variant="h6" gutterBottom>
                  Sorteio Vinculado
                </Typography>
                <Typography variant="body1">
                  Sorteio: {competition.targetDraw.title}
                </Typography>
                <Typography variant="body1">
                  Prêmio Total:{" "}
                  {formatCurrency(competition.targetDraw.totalPrize)}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  Descrição do Sorteio: {competition.targetDraw.description}
                </Typography>
                <Typography variant="body1">
                  Data do Sorteio:{" "}
                  {formatDateTime(competition.targetDraw.drawDate)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 2, p: 1 }}>
              <Typography variant="h6" gutterBottom>
                Posições:
              </Typography>
              <CompetitionPositionManager
                token={token}
                competitionId={competition.id}
                competitionTitle={competition.title}
                positions={competition.positions as Position[]}
                onPositionsChanged={handlePositionsChanged}
              />
            </Box>
          </CustomTabPanel>

          {/* Aba 2: Escalações (inalterada) */}
          <CustomTabPanel value={tabValue} index={1}>
            <CompetitionLineupsTab
              token={token}
              competitionId={competition.id}
              formatCurrency={formatCurrency}
            />
          </CustomTabPanel>

          {/* Aba 3: Resultados */}
          <CustomTabPanel value={tabValue} index={2}>
            {isResultsLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : resultsData ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Resultados da Competição
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {resultsData.message}
                </Alert>

                {/* Detalhes dos Resultados - SUBSTITUINDO GRID */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {/* ID da Competição (xs=12, sm=4) */}
                  <Box
                    sx={{ width: { xs: "100%", sm: "calc(33.333% - 11px)" } }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      ID da Competição:
                    </Typography>
                    <Typography variant="body1">
                      {resultsData.competitionId}
                    </Typography>
                  </Box>
                  {/* Página (xs=12, sm=4) */}
                  <Box
                    sx={{ width: { xs: "100%", sm: "calc(33.333% - 11px)" } }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Página:
                    </Typography>
                    <Typography variant="body1">{resultsData.page}</Typography>
                  </Box>
                  {/* Limite por Página (xs=12, sm=4) */}
                  <Box
                    sx={{ width: { xs: "100%", sm: "calc(33.333% - 11px)" } }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Limite por Página:
                    </Typography>
                    <Typography variant="body1">{resultsData.limit}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 3 }} />
              </Box>
            ) : (
              <Alert severity="warning">
                Não foi possível carregar os resultados. Tente novamente.
              </Alert>
            )}
          </CustomTabPanel>

          {/* Aba 4: Ganhadores */}
          <CustomTabPanel value={tabValue} index={3}>
            {isWinnersLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : winnersData ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Ganhadores da Competição
                </Typography>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {winnersData.message}
                </Alert>

                {/* Detalhes dos Ganhadores - SUBSTITUINDO GRID */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {/* ID da Competição (xs=12, sm=6) */}
                  <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                    <Typography variant="body1" fontWeight="bold">
                      ID da Competição:
                    </Typography>
                    <Typography variant="body1">
                      {winnersData.competitionId}
                    </Typography>
                  </Box>
                  {/* Item Vazio/Ocupação (xs=12) */}
                  <Box sx={{ width: "100%" }}></Box>
                </Box>
              </Box>
            ) : (
              <Alert severity="warning">
                Não foi possível carregar a lista de ganhadores. Tente
                novamente.
              </Alert>
            )}
          </CustomTabPanel>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            display: "flex",
            justifyContent: fullScreen ? "flex-start" : "flex-end",
            gap: 1,
          }}
        >
          {/* BOTÕES DE AÇÃO: APENAS EM TELAS PEQUENAS (Empilhados e responsivos) */}
          {fullScreen && ActionButtons}

          {/* BOTÃO FECHAR: OCULTO EM TELAS PEQUENAS (fullScreen) */}
          {!fullScreen && (
            <Button
              onClick={onClose}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#008870",
                "&:hover": {
                  backgroundColor: "#005848ff",
                },
              }}
            >
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 2. DIALOG DE EDIÇÃO (PUT) */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>Editar Detalhes da Competição</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Título da Competição"
            fullWidth
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            margin="normal"
          />
          <TextField
            label="Número de Ganhadores"
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            value={editWinnersCount}
            onChange={(e) => setEditWinnersCount(Number(e.target.value))}
            margin="normal"
            helperText="Este é o número de vencedores definidos para a competição."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsEditModalOpen(false)}
            sx={{
              color: "#008870",
              "&:hover": {
                color: "#005848ff",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditCompetition}
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{
              backgroundColor: "#008870",
              "&:hover": {
                backgroundColor: "#005848ff",
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Salvar Alterações"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. SNACKBAR DE FEEDBACK */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompetitionDetailsModal;
